/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import errors from '../../../../common/nodejs/errors';
import { PHASE, DEFINED_ROUTINE_STATUS } from './constants';

export default {
  name: 'requirement167',
  data() {
    return {
      phase: PHASE.STANDBY,
      beforePhase: PHASE.STANDBY,
      isLoading: false,
      displayInfo: '',
      systemId: '',
      // ダイアログフラグ
      modal_requirement_167: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.AIMING_STOP_ERROR:
        case PHASE.TIMEOUT_ERROR:
        case PHASE.FAILED:
        case PHASE.DISPLAY_STATUS_ERROR:
          return 'danger';
        case PHASE.DISPLAY_STATUS_SUCCESS:
        case PHASE.CORRECT_STOP:
          return 'success';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** REQ.WS.167.SBY.1：待機画面の表示 */
    init() {
      this.modal_requirement_167 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.updatePhase(PHASE.STANDBY);
    },

    /** REQ.WS.167.SBY.3：待機画面のOKボタン押下 */
    async onClickStandbyOK() {
      try {
        this.isLoading = true;

        // セッション遷移～セキュリティアクセス実行
        const response = await this.polling('standby');
        if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
          // 初期化ルーチン実行を開始
          await this.exeInitialize();
          return;
        }

        this.updatePhase(PHASE.FAILED);
      } finally {
        // カメラ全調整モードの異常終了や、途中でStatus表示に遷移後にここでローディングを解除する。
        this.isLoading = false;
      }
    },

    /** REQ.WS.167.INI.EXE.1：初期化 ルーチン実行 */
    async exeInitialize() {
      const response = await this.polling('execute_init');
      if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
        await this.checkInitialize();
        return;
      }

      this.updatePhase(PHASE.FAILED);
    },

    /** REQ.WS.167.INI.EXM.1：初期化 ルーチン結果確認 */
    async checkInitialize() {
      // 初期化 実行監視画面を表示
      this.updatePhase(PHASE.INIT_MONITORING);
      const response = await this.polling('checkWithRepeat');
      if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
        await this.exeClear();
        return;
      }

      this.displayError(response);
    },

    /** REQ.WS.167.CLR.EXE.1：調整値クリア ルーチン実行 */
    async exeClear() {
      // 調整値クリア実行
      const response = await this.polling('execute_clear');
      if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
        await this.checkClear();
        return;
      }

      this.updatePhase(PHASE.FAILED);
    },

    /** REQ.WS.167.CLR.EXM.1：調整値クリア ルーチン結果確認 */
    async checkClear() {
      // 調整値クリア 実行監視画面を表示
      this.updatePhase(PHASE.CLEAR_MONITORING);

      const response = await this.polling('check_clear');
      const requestStatus = response?.data?.status;
      if (requestStatus === REQUEST_STATUS_COMPLETED) {
        const routineStatus = response.data.user_data?.routine_status;
        if (routineStatus === "1F00") {
          await this.exeAiming();
        } else {
          this.showDisplayStatus(routineStatus);
        }
        return;
      }

      this.displayError(response);
    },

    /** REQ.WS.167.AIM.EXE.1：カメラ全調整モード ルーチン実行 */
    async exeAiming() {
      const response = await this.polling('execute_aiming');
      if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
        await this.checkAiming();
        return;
      }

      this.updatePhase(PHASE.FAILED);
    },

    /** REQ.WS.167.AIM.EXM.1：カメラ全調整モード ルーチン結果確認 */
    async checkAiming() {
      this.updatePhase(PHASE.AIMING_MONITORING);

      const response = await this.polling('checkWithRead_aiming');
      const requestStatus = response?.data?.status;

      if (requestStatus === REQUEST_STATUS_COMPLETED) {
        const routineStatus = response.data.user_data?.routine_status;
        if (routineStatus === "140F") {
          this.updatePhase(PHASE.AIMING_STOP_ERROR);
          return;
        }

        this.showDisplayStatus(routineStatus, response.data.user_data?.accuracy_rate);
        return;
      }

      this.displayError(response);
    },

    /** REQ.WS.167.CORRECT.SBY.3：距離補正 待機～実行 */
    async onClickCorrectOK() {
      try {
        this.isLoading = true;

        // 距離補正 ルーチン実行
        const response = await this.polling('execute_correct');
        if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
          await this.checkCorrect();
          return;
        }

        this.updatePhase(PHASE.FAILED);
      } finally {
        this.isLoading = false;
      }
    },

    /** REQ.WS.167.CORRECT.EXM.1：距離補正 実行監視 */
    async checkCorrect() {
      // 距離補正 実行監視画面へ遷移
      this.updatePhase(PHASE.CORRECT_MONITORING);

      const response = await this.polling('check_correct');
      const requestStatus = response?.data?.status;
      if (requestStatus === REQUEST_STATUS_COMPLETED) {
        if (response.data.user_data?.routine_status === "1500") {
          // 距離補正 停止画面へ遷移
          this.updatePhase(PHASE.CORRECT_STOP);
        } else {
          this.showDisplayStatus(response.data.user_data?.routine_status);
        }
        return;
      }

      this.displayError(response);
    },

    /** REQ.WS.167.RATE.SBY.3：正解率検査 待機OK押下 */
    async onClickRateOK() {
      try {
        this.isLoading = true;
        // 正解率検査 ルーチン実行
        const response = await this.polling('execute_rate');
        if (response?.data?.status === REQUEST_STATUS_COMPLETED) {
          await this.checkRate();
          return;
        }
        this.updatePhase(PHASE.FAILED);
      } finally {
        this.isLoading = false;
      }
    },

    /** REQ.WS.167.RATE.EXM.1：正解率検査 実行監視 */
    async checkRate() {
      this.updatePhase(PHASE.RATE_MONITORING);

      const response = await this.polling('checkWithRead_rate');
      const requestStatus = response?.data?.status;

      if (requestStatus === REQUEST_STATUS_COMPLETED) {
        const userData = response.data.user_data;
        this.showDisplayStatus(userData?.routine_status, userData?.accuracy_rate);
        return;
      }

      this.displayError(response);
    },

    /**
     * APIレスポンスの内容を確認し、通信エラーかタイムアウトエラーを表示する。
     * @param {object} apiResponse ポーリング終了時のAPIレスポンス
     */
    displayError(apiResponse) {
      const responseData = apiResponse?.data;
      if (responseData?.status === REQUEST_STATUS_FAILED && responseData?.async_code === errors.WSP_167_TIMEOUT_ERROR) {
        this.updatePhase(PHASE.TIMEOUT_ERROR);
        return;
      }

      this.updatePhase(PHASE.FAILED);
    },

    /**
     * Status表示へ遷移する(REQ.WS.167.ERR.STATUS)
     * @param {string} routineStatus ルーチン結果確認で取得したRoutineStatus
     * @param {string | number} accuracyRate DID変換した正解率。正解率が返ってこないAPIの場合はデフォルト引数の空文字で文言を置換する。
     */
    showDisplayStatus(routineStatus, accuracyRate = "") {
      if (!DEFINED_ROUTINE_STATUS.has(routineStatus)) {
        // 文言表のRoutineStatus列に記載されている値以外の場合
        this.phase = PHASE.DISPLAY_STATUS_ERROR;
        this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_167_TXT_14`).replace('$ROUTINESTATUS$', routineStatus);
        return;
      }

      const isSuccess = ["1400", "1300"].includes(routineStatus);
      this.phase = isSuccess ? PHASE.DISPLAY_STATUS_SUCCESS : PHASE.DISPLAY_STATUS_ERROR;

      // ルーチンステータスの値で文言を引き当てる
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_167_TXT_14_${routineStatus}`);

      // $DID$が指定されていない文言では何も置換されない。
      // データ読出し無しのルーチン実行であればデフォルト引数の空文字で置換される。
      // 空文字での置換はQAで合意済み。
      this.displayInfo = this.displayInfo.replace("$DID$", accuracyRate);
    },

    /**
     * REQ_WS_167_TXT_14以外の文言とphase切り替えを行う
     * @param {string} phase 切り換え先のphase
     */
    updatePhase(phase) {
      this.phase = phase;

      const phaseDisplayInfoMap = {
        [PHASE.STANDBY]: "REQ_WS_167_TXT_1",
        [PHASE.INIT_MONITORING]: "REQ_WS_167_TXT_2",
        [PHASE.CLEAR_MONITORING]: "REQ_WS_167_TXT_3",
        [PHASE.AIMING_MONITORING]: "REQ_WS_167_TXT_4",
        [PHASE.AIMING_STOP_ERROR]: "REQ_WS_167_TXT_5_140F",
        [PHASE.CORRECT_STANDBY]: "REQ_WS_167_TXT_6",
        [PHASE.CORRECT_MONITORING]: "REQ_WS_167_TXT_7",
        [PHASE.CORRECT_STOP]: "REQ_WS_167_TXT_8_1500",
        [PHASE.RATE_STANDBY]: "REQ_WS_167_TXT_9",
        [PHASE.RATE_MONITORING]: "REQ_WS_167_TXT_10",
        [PHASE.CANCEL]: "REQ_WS_167_TXT_11",
        [PHASE.FAILED]: "REQ_WS_167_TXT_12",
        [PHASE.TIMEOUT_ERROR]: "REQ_WS_167_TXT_13"
      };

      this.displayInfo = this.$t(`${this.i18nWsKey}.${phaseDisplayInfoMap[phase]}`);
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        () => { this.logout(this); },
        // セッションリセット処理のコールバック
        () => { this.resetSession(this); },
        // その他エラー発生のコールバック
        () => { this.showUnexpectedError(); },
      );
    },

    /**
     * API呼び出し
     * @param {String} type POSTのリクエストボディで指定するtype
     * @returns {Promise<object>}ポーリング終了時点のレスポンス
     */
    async polling(type) {
      const token = session.getToken();
      const body = { id_token: token.id_token, type: type };

      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        body,
        () => { this.showUnexpectedError(); },
        (result) => { this.validateErrorResult(result); },
        (result) => { this.validateErrorResult(result); },
      );
      return response;
    },

    /** キャンセル表示 */
    displayCancel() {
      // 遷移前に呼び出し元画面のPhaseを保持
      this.beforePhase = this.phase;

      // キャンセル表示画面に遷移
      this.updatePhase(PHASE.CANCEL);
    },

    /** REQ.WS.167.END.CANCEL.4：キャンセル表示から呼び出し元の画面へ戻る */
    abortCancel() {
      this.updatePhase(this.beforePhase);
    },

    /** REQ.WS.167.END.FIN.1～2：調整モード終了 */
    async exeTerminate() {
      if (this.phase === PHASE.CANCEL && this.beforePhase === PHASE.STANDBY) {
        // 一度も車両通信が発生していない状態であればデフォルトセッション遷移は実行せず、そのまま機能終了する。
        this.closeWizard();
        return;
      }

      try {
        this.isLoading = true;
        await this.polling('terminate');
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_167 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      this.updatePhase(PHASE.FAILED);
    }
  },

  mounted() {
    this.init();
  },
};